import React                        from 'react'
import { Box,
         TextField,
         IconButton,
         Typography,
         Button,
         useTheme,
         Grid,
       }                            from '@mui/material'
import AddIcon                      from '@mui/icons-material/Add'
import { AnswerChoice, SubChoice } from 'types/apis/surveyInterfaces'
import { SubChoicesSection }        from './SubChoicesSection'
import { Trash2Icon }               from 'lucide-react'

export interface AnswerChoicesSectionProps {
  choices: AnswerChoice[]
  onChoicesChange: (choices: AnswerChoice[]) => void
  questionType: string
  isReadOnly?: boolean
  errors?: any
  isChoiceFieldModified?: (choiceIndex: number, fieldName: keyof AnswerChoice) => boolean
  isSubChoiceFieldModified?: (choiceIndex: number, subChoiceIndex: number, fieldName: keyof SubChoice) => boolean
  getModifiedChoiceFieldStyle?: (isModified: boolean) => any
  getModifiedChoiceSelectStyle?: (isModified: boolean) => any
}

export const AnswerChoicesSection: React.FC<AnswerChoicesSectionProps> = ({
  choices,
  onChoicesChange,
  questionType,
  isReadOnly = false,
  errors,
  isChoiceFieldModified,
  isSubChoiceFieldModified,
  getModifiedChoiceFieldStyle,
  getModifiedChoiceSelectStyle,
}) => {

  const showSubChoices = questionType === 'MULTIPLE_WITH_SUB_CHOICES'
  const theme = useTheme()

  // Common label component for form fields
  const renderFormLabel = (label: string, required: boolean = false) => (
    <Typography 
      variant="body2" 
      sx={{ 
        color: theme.palette.text.secondary,
        fontWeight: 500,
      }}
    >
      {label} {required && <span style={{ fontSize: '0.75rem', color: theme.palette.error.main }}>*</span>}
    </Typography>
  )

  const handleAddChoice = () => {
    const newChoice: AnswerChoice = {
      title: '',
      value: '',
      readMapper: '',
      writeMapper: '',
      unselectMapper: '',
      order: choices.length + 1,
      ...(showSubChoices ? { subChoices: [] } : {}),
    } as AnswerChoice
    onChoicesChange([...choices, newChoice])
  }

  const handleDeleteChoice = (index: number) => {
    const updated = choices.filter((_, i) => i !== index)
    // Reorder remaining choices
    const reordered = updated.map((choice, i) => ({ ...choice, order: i + 1 }))
    onChoicesChange(reordered)
  }

  const handleChoiceChange = (index: number, field: keyof AnswerChoice, value: any) => {
    const updated = [...choices]
    updated[index] = { ...updated[index], [field]: value }
    onChoicesChange(updated)
  }

  const handleSubChoicesChange = (index: number, subChoices: any[]) => {
    const updated = [...choices]
    updated[index] = { ...updated[index], subChoices }
    onChoicesChange(updated)
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
        Answer Choices
      </Typography>
      {choices.map((choice, index) => (
        <Box
          key={index}
          sx={{
            backgroundColor: theme.palette.background.default,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            mb: 2,
            position: 'relative',
          }}
        >
          {choices.length > 0 && !isReadOnly && (
            <IconButton
              size="small"
              onClick={() => handleDeleteChoice(index)}
              sx={{
                position: 'absolute',
                right: 2,
                top: 2,
                color: 'error.main',
              }}
            >
              <Trash2Icon size={20} />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <>
              {renderFormLabel('Display Text', true)}
              <TextField
                value={choice.title}
                onChange={(e) => handleChoiceChange(index, 'title', e.target.value)}
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: theme.palette.background.paper,
                  },
                  ...(getModifiedChoiceFieldStyle && isChoiceFieldModified 
                    ? getModifiedChoiceFieldStyle(isChoiceFieldModified(index, 'title'))
                    : {}),
                }}
                required
                disabled={isReadOnly}
                error={!!errors?.choices?.[index]?.title}
                helperText={errors?.choices?.[index]?.title?.message || 'Max 50 characters'}
                inputProps={{ maxLength: 50 }}
              />
            </>
            <>
              {renderFormLabel('Value', true)}
              <TextField
                value={choice.value}
                onChange={(e) => handleChoiceChange(index, 'value', e.target.value)}
                fullWidth
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: theme.palette.background.paper,
                  },
                  ...(getModifiedChoiceFieldStyle && isChoiceFieldModified 
                    ? getModifiedChoiceFieldStyle(isChoiceFieldModified(index, 'value'))
                    : {}),
                }}
                size="small"
                required
                disabled={isReadOnly}
                error={!!errors?.choices?.[index]?.value}
                helperText={errors?.choices?.[index]?.value?.message}
              />
            </>
            <Grid container spacing={2} mt={1}>
              <Grid size={4}>
                <>
                  {renderFormLabel('Read Mapper')}
                  <TextField
                    value={choice.readMapper || ''}
                    onChange={(e) => handleChoiceChange(index, 'readMapper', e.target.value)}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        mt: 1,
                        backgroundColor: theme.palette.background.paper,
                      },
                      ...(getModifiedChoiceFieldStyle && isChoiceFieldModified 
                        ? getModifiedChoiceFieldStyle(isChoiceFieldModified(index, 'readMapper'))
                        : {}),
                    }}
                    size="small"
                    disabled={isReadOnly}
                  />
                </>
              </Grid> 
              <Grid size={4}>
                <>
                  {renderFormLabel('Write Mapper')}
                  <TextField
                    value={choice.writeMapper || ''}
                    onChange={(e) => handleChoiceChange(index, 'writeMapper', e.target.value)}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        mt: 1,
                        backgroundColor: theme.palette.background.paper,
                      },
                      ...(getModifiedChoiceFieldStyle && isChoiceFieldModified 
                        ? getModifiedChoiceFieldStyle(isChoiceFieldModified(index, 'writeMapper'))
                        : {}),
                    }}
                    size="small"
                    disabled={isReadOnly}
                  />
                </>
              </Grid> 
              <Grid size={4}> 
                <>
                  {renderFormLabel('Unselect Mapper')}
                  <TextField
                    value={choice.unselectMapper}
                    onChange={(e) => handleChoiceChange(index, 'unselectMapper', e.target.value)}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        mt: 1,
                        backgroundColor: theme.palette.background.paper,
                      },
                      ...(getModifiedChoiceFieldStyle && isChoiceFieldModified 
                        ? getModifiedChoiceFieldStyle(isChoiceFieldModified(index, 'unselectMapper'))
                        : {}),
                    }}
                    size="small"
                    disabled={isReadOnly}
                  />
                </>
              </Grid> 

            </Grid>
            
            {showSubChoices && (
              <SubChoicesSection
                subChoices={choice.subChoices || []}
                onSubChoicesChange={(subChoices) => handleSubChoicesChange(index, subChoices)}
                isReadOnly={isReadOnly}
                parentChoiceIndex={index}
                isSubChoiceFieldModified={isSubChoiceFieldModified}
                getModifiedChoiceFieldStyle={getModifiedChoiceFieldStyle}
                getModifiedChoiceSelectStyle={getModifiedChoiceSelectStyle}
              />
            )}
          </Box>
        </Box>
      ))}
      {!isReadOnly && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddChoice}
          variant="outlined"
          size="small"
        >
          Add Choice
        </Button>
      )}
    </Box>
  )
}

